import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, PlayCircle, Music as MusicIcon, MessageSquare, Clock, Lock, Disc3, ListMusic, Image as ImageIcon, Film, EyeOff, Play, Pause, Clapperboard } from "lucide-react";
import { doSearch, getRecentSearches, addRecentSearch, clearRecentSearches, useDebouncedValue } from "@/lib/searchApi";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { MembersOnlySheet } from "@/components/common/MembersOnlySheet";
import { TileImage } from "@/components/common/Skeleton";

const glass = {
  background: "rgba(17, 20, 34, 0.92)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
};

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
};

const Thumb = ({ src, Icon, rounded = "rounded-lg" }) => (
  <div className={`relative w-12 h-12 ${rounded} flex-shrink-0 overflow-hidden flex items-center justify-center`} style={{ background: "rgba(255,255,255,0.08)" }}>
    {src ? <TileImage src={src} className="w-full h-full object-cover" /> : <Icon size={20} style={{ color: "rgba(255,255,255,0.5)" }} />}
  </div>
);

// Lock badge: tier=premium -> Don-only, tier=subscribers -> Subscribers.
const LockBadge = ({ tier }) => {
  const { t } = useTranslation();
  const label = tier === "premium" ? t("search.lockDon") : t("search.lockSubscribers");
  return (
    <span data-testid="search-lock-badge" className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0"
      style={{ background: tier === "premium" ? "rgba(245,158,11,0.18)" : "rgba(37,117,252,0.18)", color: tier === "premium" ? "#f59e0b" : "#60a5fa" }}>
      <Lock size={11} /> {label}
    </span>
  );
};

// Owner/admin-only status badge for hidden/draft/scheduled catalogue entries.
const OwnerBadge = ({ badge }) => {
  const { t } = useTranslation();
  const map = { hidden: t("search.badgeHidden"), draft: t("search.badgeDraft"), scheduled: t("search.badgeScheduled") };
  return (
    <span data-testid="search-owner-badge" className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0"
      style={{ background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)" }}>
      <EyeOff size={11} /> {map[badge] || badge}
    </span>
  );
};

const Row = ({ testid, onClick, thumbSrc, Icon, rounded, title, subtitle, right, item }) => (
  <button onClick={onClick} data-testid={testid} className="flex items-center gap-3 w-full text-left p-1.5 rounded-lg transition-colors hover:bg-white/5">
    <Thumb src={thumbSrc} Icon={Icon} rounded={rounded} />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium truncate" style={{ color: "#fff" }}>{title || "Untitled"}</p>
      {subtitle && <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.5)" }}>{subtitle}</p>}
    </div>
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {item?.badge && <OwnerBadge badge={item.badge} />}
      {item?.locked && <LockBadge tier={item.tier} />}
      {right}
    </div>
  </button>
);

const relType = (t, type) => (type === "ep" ? t("search.typeEp") : type === "single" ? t("search.typeSingle") : t("search.typeAlbum"));

const TAB_DEFS = [
  { key: "videos", label: "search.tabVideos" },
  { key: "clips", label: "search.tabClips" },
  { key: "shows", label: "search.tabShows" },
  { key: "tracks", label: "search.tabTracks" },
  { key: "albums", label: "search.tabAlbums" },
  { key: "playlists", label: "search.tabPlaylists" },
  { key: "image_posts", label: "search.tabPhotos" },
  { key: "text_posts", label: "search.tabPosts" },
];

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const player = useAudioPlayer();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("videos");
  const [recent, setRecent] = useState([]);
  const [members, setMembers] = useState(null); // gated tier: "premium" | "subscribers" | null
  const [lightbox, setLightbox] = useState(null); // { images:[], idx } for image posts
  const inputRef = useRef(null);
  const debounced = useDebouncedValue(q, 300);

  const openOverlay = useCallback(() => {
    setOpen(true);
    setRecent(getRecentSearches());
    setTimeout(() => inputRef.current?.focus(), 60);
  }, []);
  const closeOverlay = useCallback(() => {
    setOpen(false);
    setQ("");
    setData(null);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) closeOverlay(); };
    window.addEventListener("keydown", onKey);
    const onOpen = () => openOverlay();
    window.addEventListener("open-global-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-global-search", onOpen);
    };
  }, [open, closeOverlay, openOverlay]);

  useEffect(() => {
    const term = debounced.trim();
    if (!open || !term) { setData(null); return; }
    let active = true;
    setLoading(true);
    doSearch({ q: term, scope: "global" })
      .then((res) => {
        if (!active) return;
        setData(res);
        setTab(res.default_tab || "videos");
      })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debounced, open]);

  const go = (path) => { addRecentSearch(q); closeOverlay(); navigate(path); };

  // ── STEP FOUR click behaviour ──
  const openMembers = (tier) => setMembers(tier || "subscribers");
  const mapTrack = (t) => ({ id: t.id, title: t.title, cover: t.cover_art, cover_art: t.cover_art, locked: t.locked, tier: t.tier });
  // Playable search queue = unlocked tracks in result order (next/prev walk this).
  const playableTracks = () => (data?.tracks || []).filter((t) => !t.locked).map(mapTrack);

  // Cover play button: play the full track inline (no player opens); toggle pause.
  const onTrackPlay = (t) => {
    if (t.locked) return openMembers(t.tier);
    if (player.current?.id === t.id && player.isPlaying) { player.pause(); return; }
    const list = playableTracks();
    const i = list.findIndex((x) => x.id === t.id);
    player.playTrack(mapTrack(t), list, i < 0 ? 0 : i, { ui: "hidden" });
  };
  // Row click: open the full player; next/prev walk the search results in order.
  const onTrackOpen = (t) => {
    if (t.locked) return openMembers(t.tier);
    const list = playableTracks();
    const i = list.findIndex((x) => x.id === t.id);
    addRecentSearch(q);
    closeOverlay();
    player.playTrack(mapTrack(t), list, i < 0 ? 0 : i);
  };
  // Video/clip: the whole result set becomes the scroll thread (result order).
  const onVideoOpen = (v, bucket) => {
    // Locked videos/clips open the detail thread too (inline tier wall + scroll
    // past), same as unlocked. Only track rows keep openMembers.
    try { sessionStorage.setItem("search_video_thread", JSON.stringify((bucket || []).map((x) => x.id))); } catch (e) { /* ignore */ }
    go(`/videos/${v.id}?ctx=search`);
  };

  // Image post: open its image(s) in place (consistent with inline track play,
  // stay in the results flow rather than navigating away). Text posts go to Community.
  const openImagePost = (p) => {
    const imgs = (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []));
    if (!imgs.length) return go("/community");
    setLightbox({ images: imgs, idx: 0 });
  };

  const counts = TAB_DEFS.reduce((acc, t) => { acc[t.key] = data?.[t.key]?.length || 0; return acc; }, {});
  const total = TAB_DEFS.reduce((n, t) => n + counts[t.key], 0);
  const availableTabs = TAB_DEFS.filter((t) => counts[t.key] > 0);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            data-testid="global-search-overlay"
            className="fixed inset-0 z-[110] flex flex-col"
            style={glass}
          >
            <div className="max-w-2xl w-full mx-auto px-4 pt-5 flex flex-col h-full">
              <div className="flex items-center gap-3 mb-4">
                <Search size={20} style={{ color: "rgba(255,255,255,0.6)" }} />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { addRecentSearch(q); setRecent(getRecentSearches()); } }}
                  data-testid="global-search-input"
                  placeholder={t("search.placeholder")}
                  className="flex-1 bg-transparent outline-none text-lg"
                  style={{ color: "#fff" }}
                />
                <button onClick={closeOverlay} data-testid="global-search-close" className="p-1" aria-label="Close search">
                  <X size={22} style={{ color: "rgba(255,255,255,0.7)" }} />
                </button>
              </div>

              {!q.trim() ? (
                <div data-testid="global-search-recent">
                  {recent.length > 0 && (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.45)" }}>{t("search.recent")}</span>
                        <button onClick={() => setRecent(clearRecentSearches())} data-testid="global-search-clear-recent" className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>{t("search.clear")}</button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {recent.map((t) => (
                          <button key={t} onClick={() => setQ(t)} data-testid={`recent-search-${t}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm" style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)" }}>
                            <Clock size={13} /> {t}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {recent.length === 0 && (
                    <p className="text-sm text-center py-16" style={{ color: "rgba(255,255,255,0.45)" }}>{t("search.idleHint")}</p>
                  )}
                </div>
              ) : (
                <>
                  {data?.artist && (
                    <button onClick={() => go(data.artist.url)} data-testid="global-search-artist" className="flex items-center gap-3 w-full p-3 mb-3 rounded-xl" style={{ background: "rgba(37,117,252,0.15)" }}>
                      <Thumb src={data.artist.avatar_url} Icon={MusicIcon} rounded="rounded-full" />
                      <div className="text-left">
                        <p className="text-sm font-bold" style={{ color: "#fff" }}>{data.artist.display_name}</p>
                        <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{t("search.artistProfile")}</p>
                      </div>
                    </button>
                  )}

                  {availableTabs.length > 0 && (
                    <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="global-search-tabs">
                      {availableTabs.map((tb) => (
                        <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`global-search-tab-${tb.key}`}
                          className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0"
                          style={{ background: tab === tb.key ? "var(--accent-primary)" : "rgba(255,255,255,0.08)", color: tab === tb.key ? "#fff" : "rgba(255,255,255,0.7)" }}>
                          {t(tb.label)} ({counts[tb.key]})
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto pb-8" data-testid="global-search-results">
                    {loading && !data ? (
                      <p className="text-sm text-center py-10" style={{ color: "rgba(255,255,255,0.5)" }}>{t("search.searching")}</p>
                    ) : total === 0 ? (
                      <p className="text-sm text-center py-10" data-testid="global-search-empty" style={{ color: "rgba(255,255,255,0.5)" }}>{t("search.empty", { q })}</p>
                    ) : (
                      <div className="space-y-1">
                        {tab === "videos" && (data.videos || []).map((v) => (
                          <Row key={v.id} testid={`search-video-${v.id}`} onClick={() => onVideoOpen(v, data.videos)} thumbSrc={v.thumbnail} Icon={PlayCircle} title={v.title} item={v} />
                        ))}
                        {tab === "clips" && (data.clips || []).map((v) => (
                          <Row key={v.id} testid={`search-clip-${v.id}`} onClick={() => onVideoOpen(v, data.clips)} thumbSrc={v.thumbnail} Icon={Film} title={v.title} item={v} />
                        ))}
                        {tab === "shows" && (data.shows || []).map((s) => (
                          <Row key={s.id} testid={`search-show-${s.id}`} onClick={() => go(s.url)} thumbSrc={s.cover_art} Icon={Clapperboard} title={s.title} item={s} />
                        ))}
                        {tab === "tracks" && (data.tracks || []).map((t) => {
                          const playingThis = player.current?.id === t.id && player.isPlaying;
                          return (
                            <div key={t.id} data-testid={`search-track-${t.id}`} className="flex items-center gap-3 w-full p-1.5 rounded-lg transition-colors hover:bg-white/5">
                              <button onClick={(e) => { e.stopPropagation(); onTrackPlay(t); }} data-testid={`search-track-play-${t.id}`} aria-label="Play"
                                className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)" }}>
                                {t.cover_art ? <TileImage src={t.cover_art} className="w-full h-full object-cover" /> : <MusicIcon size={20} style={{ color: "rgba(255,255,255,0.5)" }} />}
                                <span className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.38)" }}>
                                  {playingThis ? <Pause size={18} color="#fff" /> : <Play size={18} color="#fff" style={{ marginLeft: 1 }} />}
                                </span>
                              </button>
                              <button onClick={() => onTrackOpen(t)} data-testid={`search-track-open-${t.id}`} className="min-w-0 flex-1 text-left">
                                <p className="text-sm font-medium truncate" style={{ color: "#fff" }}>{t.title || "Untitled"}</p>
                              </button>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {t.badge && <OwnerBadge badge={t.badge} />}
                                {t.locked && <LockBadge tier={t.tier} />}
                                {t.duration && !t.locked && <span className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>{t.duration}</span>}
                              </div>
                            </div>
                          );
                        })}
                        {tab === "albums" && (data.albums || []).map((a) => (
                          <Row key={a.id} testid={`search-album-${a.id}`} onClick={() => go(a.url || "/music")} thumbSrc={a.cover_art} Icon={Disc3} title={a.title} subtitle={relType(t, a.type || a.release_type)} item={a} />
                        ))}
                        {tab === "playlists" && (data.playlists || []).map((p) => (
                          <Row key={p.id} testid={`search-playlist-${p.id}`} onClick={() => go(p.url || "/music")} thumbSrc={p.cover_art} Icon={ListMusic} title={p.title} subtitle={t("search.typePlaylist")} item={p} />
                        ))}
                        {tab === "image_posts" && (data.image_posts || []).map((p) => (
                          <Row key={p.id} testid={`search-image-post-${p.id}`} onClick={() => openImagePost(p)} thumbSrc={p.image_url} Icon={ImageIcon} title={p.preview || "Photo"} subtitle={relTime(p.created_at)} item={p} />
                        ))}
                        {tab === "text_posts" && (data.text_posts || []).map((p) => (
                          <Row key={p.id} testid={`search-post-${p.id}`} onClick={() => go("/community")} Icon={MessageSquare} title={p.preview || "Post"} subtitle={relTime(p.created_at)} item={p} />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MembersOnlySheet
        open={!!members}
        onClose={() => setMembers(null)}
        title={members === "premium" ? t("members.donTitle") : undefined}
        body={members === "premium" ? t("members.donBody") : undefined}
      />

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)} data-testid="search-image-viewer"
            className="fixed inset-0 z-[10050] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.85)" }}
          >
            <button onClick={() => setLightbox(null)} data-testid="search-image-viewer-close" className="absolute top-4 right-4 p-2" aria-label="Close" style={{ color: "#fff" }}><X size={26} /></button>
            <img src={lightbox.images[lightbox.idx]} alt="" onClick={(e) => e.stopPropagation()} className="max-w-full max-h-[85vh] rounded-lg object-contain" />
            {lightbox.images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, idx: (l.idx - 1 + l.images.length) % l.images.length })); }} data-testid="search-image-viewer-prev" className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center text-2xl" style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }}>‹</button>
                <button onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, idx: (l.idx + 1) % l.images.length })); }} data-testid="search-image-viewer-next" className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center text-2xl" style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }}>›</button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalSearch;
